import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

interface TripDialogProps {
  isOpen: boolean;
  onClose: () => void;
  trip?: {
    id: string;
    trip_name: string;
    trip_date: string;
    notes: string | null;
    is_public?: boolean;
    ticket_link?: string | null;
  } | null;
}

export const TripDialog = ({ isOpen, onClose, trip }: TripDialogProps) => {
  const { session } = useAuth();
  const [formData, setFormData] = useState({
    trip_name: "",
    trip_date: "",
    notes: "",
    is_public: false,
    ticket_link: "",
  });
  const [saving, setSaving] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!session?.user?.id) return;
      const { data } = await supabase.rpc("has_role", {
        _user_id: session.user.id,
        _role: "admin",
      });
      setIsAdmin(data || false);
    };
    checkAdminStatus();
  }, [session]);

  useEffect(() => {
    if (trip) {
      setFormData({
        trip_name: trip.trip_name,
        trip_date: trip.trip_date,
        notes: trip.notes || "",
        is_public: trip.is_public || false,
        ticket_link: trip.ticket_link || "",
      });
    } else {
      setFormData({
        trip_name: "",
        trip_date: new Date().toISOString().split("T")[0],
        notes: "",
        is_public: false,
        ticket_link: "",
      });
    }
  }, [trip, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!session?.user?.id) return;

    setSaving(true);
    try {
      const tripData = {
        user_id: session.user.id,
        trip_name: formData.trip_name,
        trip_date: formData.trip_date,
        notes: formData.notes || null,
        ...(isAdmin && {
          is_public: formData.is_public,
          ticket_link: formData.ticket_link || null,
        }),
      };

      if (trip) {
        const { error } = await supabase
          .from("trips")
          .update(tripData)
          .eq("id", trip.id);

        if (error) throw error;
        toast.success("Trip updated successfully");
      } else {
        const { error } = await supabase
          .from("trips")
          .insert([tripData]);

        if (error) throw error;
        toast.success("Trip created successfully");
      }

      onClose();
    } catch (error: any) {
      toast.error(trip ? "Failed to update trip" : "Failed to create trip");
      console.error("Error saving trip:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{trip ? "Edit Trip" : "Create New Trip"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="trip_name">Trip Name *</Label>
              <Input
                id="trip_name"
                value={formData.trip_name}
                onChange={(e) =>
                  setFormData({ ...formData, trip_name: e.target.value })
                }
                placeholder="e.g., Summer Bass Fishing"
                required
              />
            </div>
            <div>
              <Label htmlFor="trip_date">Date *</Label>
              <Input
                id="trip_date"
                type="date"
                value={formData.trip_date}
                onChange={(e) =>
                  setFormData({ ...formData, trip_date: e.target.value })
                }
                required
              />
            </div>
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                placeholder="Add any notes about this trip..."
                rows={4}
              />
            </div>
            
            {isAdmin && (
              <>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="is_public">Make Trip Public</Label>
                    <p className="text-sm text-muted-foreground">
                      Allow all users to view this trip
                    </p>
                  </div>
                  <Switch
                    id="is_public"
                    checked={formData.is_public}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, is_public: checked })
                    }
                  />
                </div>
                
                <div>
                  <Label htmlFor="ticket_link">Ticket Link (Optional)</Label>
                  <Input
                    id="ticket_link"
                    type="url"
                    value={formData.ticket_link}
                    onChange={(e) =>
                      setFormData({ ...formData, ticket_link: e.target.value })
                    }
                    placeholder="https://example.com/buy-tickets"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Add a link for users to purchase trip tickets
                  </p>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving..." : trip ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
